/**
 * DM-7703 website-lead presentation decisions.
 *
 * Kept apart from the page for the reason referralCodes.logic.ts is: pure
 * functions are what the admin-ui suite can test without rendering. Enum
 * values arrive as the backend's Prisma keys (e.g. `nonprofit_501c3`,
 * `from_100_to_500`); unknown values fall back to a readable form so a new
 * option added on the website never renders blank.
 */

import type {
  EarlyAccessLeadsSummary,
  FoundingPartnerStatus,
  FoundingPartnerStatusFilter,
  WebsiteLeadAccountFilter,
  WebsiteLeadConsent,
  WebsiteLeadConsentFilter,
  WebsiteLeadContactEligibility,
  WebsiteLeadSource,
} from '../api/adminOps';
import type { ChipTone } from '../components/ui/StatusChip';

/** Typing pauses this long before a search request is sent. */
export const SEARCH_DEBOUNCE_MS = 300;
export const SEARCH_MAX_LENGTH = 100;
/** A repeat signup is a later submission, not the same request seconds apart. */
const REPEAT_THRESHOLD_MS = 60_000;

export const FOUNDING_PARTNER_STATUSES: readonly FoundingPartnerStatus[] = [
  'new',
  'contacted',
  'qualified',
  'onboarded',
  'closed',
];

const STATUS_CHIPS: Record<FoundingPartnerStatus, { label: string; tone: ChipTone }> = {
  new: { label: 'New', tone: 'info' },
  contacted: { label: 'Contacted', tone: 'warn' },
  qualified: { label: 'Qualified', tone: 'warn' },
  onboarded: { label: 'Onboarded', tone: 'ok' },
  closed: { label: 'Closed', tone: 'neutral' },
};

/** What each pipeline stage means, shown beside the status picker. */
export const STATUS_HINTS: Record<FoundingPartnerStatus, string> = {
  new: 'Not yet followed up.',
  contacted: 'We have reached out to this organization.',
  qualified: 'A good fit; working toward onboarding.',
  onboarded: 'Using DonateMate as a Founding Partner.',
  closed: 'No further follow-up planned.',
};

const DONATION_FREQUENCY_LABELS: Record<string, string> = {
  weekly: 'Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annually: 'Annually',
  occasionally: 'Occasionally',
  first_time: 'First-time donor',
};

const ORGANIZATION_TYPE_LABELS: Record<string, string> = {
  nonprofit_501c3: '501(c)(3) nonprofit',
  religious: 'Religious',
  education: 'Education',
  healthcare: 'Healthcare',
  arts: 'Arts & culture',
  environment: 'Environment',
  social: 'Social services',
  other: 'Other',
};

const ORGANIZATION_SIZE_LABELS: Record<string, string> = {
  lt_100: 'Under 100',
  from_100_to_500: '100–500',
  from_500_to_1000: '500–1,000',
  from_1000_to_5000: '1,000–5,000',
  gte_5000: '5,000+',
};

const REFERRAL_SOURCE_LABELS: Record<string, string> = {
  google_ad: 'Google ad',
  search: 'Search engine',
  reddit: 'Reddit',
  twitter: 'X / Twitter',
  facebook: 'Facebook',
  social: 'Other social media',
  friend: 'Friend or colleague',
  charity: 'A charity',
  news: 'News or article',
  other: 'Other',
};

/** `some_value` -> `Some value` for options the maps above do not know yet. */
function humanize(value: string): string {
  const spaced = value.replace(/_/g, ' ').trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

function lookup(labels: Record<string, string>, value: string | null): string | null {
  if (value === null || value === '') return null;
  return labels[value] ?? humanize(value);
}

export function statusChip(status: string): { label: string; tone: ChipTone } {
  return (
    STATUS_CHIPS[status as FoundingPartnerStatus] ?? { label: humanize(status), tone: 'neutral' }
  );
}

export function statusFilterLabel(filter: FoundingPartnerStatusFilter): string {
  return filter === 'all' ? 'All' : statusChip(filter).label;
}

export const donationFrequencyLabel = (value: string | null) =>
  lookup(DONATION_FREQUENCY_LABELS, value);
export const organizationTypeLabel = (value: string | null) =>
  lookup(ORGANIZATION_TYPE_LABELS, value);
export const organizationSizeLabel = (value: string | null) =>
  lookup(ORGANIZATION_SIZE_LABELS, value);
export const referralSourceLabel = (value: string | null) => lookup(REFERRAL_SOURCE_LABELS, value);

const LIFECYCLE_LABELS: Record<string, string> = {
  active: 'Active',
  converted: 'Converted to an account',
  unsubscribed: 'Unsubscribed',
  archived: 'Archived',
};

export const lifecycleLabel = (value: string) => lookup(LIFECYCLE_LABELS, value) ?? '—';

export function sourceLabel(source: WebsiteLeadSource): string {
  return source === 'formspree_import' ? 'Formspree import' : 'Website';
}

export function fullName(lead: { first_name: string; last_name: string }): string {
  return `${lead.first_name} ${lead.last_name}`.trim() || '(no name)';
}

export function consentChip(consent: WebsiteLeadConsent): { label: string; tone: ChipTone } {
  return consent.granted
    ? { label: 'Opted in', tone: 'ok' }
    : { label: 'No consent', tone: 'neutral' };
}

/** "v2026-09-28 · recorded 30 Sep 2026" style evidence line, or null. */
export function consentEvidence(consent: WebsiteLeadConsent): string | null {
  if (!consent.granted) return null;
  const parts = [
    consent.statement_version ? `Statement ${consent.statement_version}` : null,
    consent.recorded_at ? `recorded ${new Date(consent.recorded_at).toLocaleString()}` : null,
    consent.source ? `via ${sourceLabel(consent.source)}` : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(' · ') : null;
}

export const CONSENT_FILTERS: readonly { value: WebsiteLeadConsentFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'opted_in', label: 'Opted in' },
  { value: 'not_opted_in', label: 'Not opted in' },
];

/** DM-8483 */
export const ACCOUNT_FILTERS: readonly { value: WebsiteLeadAccountFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'has_account', label: 'Has account' },
  { value: 'no_account', label: 'No account' },
];

export function accountChip(lead: { has_account: boolean }): { label: string; tone: ChipTone } {
  return lead.has_account
    ? { label: 'Has account', tone: 'ok' }
    : { label: 'No account', tone: 'neutral' };
}

/** "Has account · signed up 14 Oct 2026, 09:00" or "No account". */
export function accountSummary(lead: {
  has_account: boolean;
  account_created_at: string | null;
}): string {
  if (!lead.has_account) return 'No account';
  return lead.account_created_at
    ? `Has account · signed up ${new Date(lead.account_created_at).toLocaleString()}`
    : 'Has account';
}

const ELIGIBILITY_CHIPS: Record<WebsiteLeadContactEligibility, { label: string; tone: ChipTone }> =
  {
    marketing_ok: { label: 'Marketing OK', tone: 'ok' },
    launch_notice_only: { label: 'Launch notice only', tone: 'warn' },
    program_outreach_ok: { label: 'Program outreach OK', tone: 'ok' },
    do_not_contact: { label: 'Do not contact', tone: 'crit' },
  };

/**
 * What each eligibility allows, from what the form and the Privacy Policy
 * promised the lead. Shown beside the chip in the lead drawer.
 */
export const ELIGIBILITY_HINTS: Record<WebsiteLeadContactEligibility, string> = {
  marketing_ok:
    'Opted in (in the app once they have an account): launch email, follow-ups and marketing are fine.',
  launch_notice_only:
    'Did not opt in: one launch notification only. No follow-up nudges or marketing.',
  program_outreach_ok:
    'The form promised follow-up about the Founding Partner program, so program outreach by email is fine.',
  do_not_contact:
    'Unsubscribed, or signed up and has marketing turned off in the app: do not contact from this list.',
};

/** Shown beside a Founding Partner phone number: no SMS consent was collected. */
export const PHONE_CONTACT_NOTE =
  'Calls dialed by hand only. Check against the Do Not Call registry first if this is a personal cell. Never text: no SMS consent was collected.';

export function eligibilityChip(eligibility: string): { label: string; tone: ChipTone } {
  return (
    ELIGIBILITY_CHIPS[eligibility as WebsiteLeadContactEligibility] ?? {
      label: humanize(eligibility),
      tone: 'neutral',
    }
  );
}

export function eligibilityHint(eligibility: string): string | null {
  return ELIGIBILITY_HINTS[eligibility as WebsiteLeadContactEligibility] ?? null;
}

/** True when the lead submitted again after their first signup. */
export function isRepeatSubmission(lead: {
  first_submitted_at: string;
  last_submitted_at: string;
}): boolean {
  const first = new Date(lead.first_submitted_at).getTime();
  const last = new Date(lead.last_submitted_at).getTime();
  return last - first > REPEAT_THRESHOLD_MS;
}

/** Whole-number share of `part` in `total`; 0 for an empty table. */
export function percentOf(part: number, total: number): number {
  if (total <= 0) return 0;
  return Math.round((part / total) * 100);
}

export interface SummaryTile {
  readonly label: string;
  readonly value: string;
  readonly hint?: string;
}

export function earlyAccessTiles(summary: EarlyAccessLeadsSummary): readonly SummaryTile[] {
  const website = summary.total - summary.formspree_import;
  return [
    { label: 'Total leads', value: summary.total.toLocaleString() },
    {
      label: 'Marketing opt-in',
      value: summary.marketing_consent.toLocaleString(),
      hint: `${percentOf(summary.marketing_consent, summary.total)}% of leads`,
    },
    {
      label: 'From website',
      value: website.toLocaleString(),
      hint: `${summary.formspree_import.toLocaleString()} imported from Formspree`,
    },
    { label: 'Joined last 7 days', value: summary.joined_last_7_days.toLocaleString() },
  ];
}

/** The stage timestamps already reached, in pipeline order. */
export function pipelineTimeline(request: {
  first_submitted_at: string;
  contacted_at: string | null;
  qualified_at: string | null;
  onboarded_at: string | null;
  closed_at: string | null;
}): ReadonlyArray<{ label: string; at: string }> {
  const stages: Array<[string, string | null]> = [
    ['Received', request.first_submitted_at],
    ['Contacted', request.contacted_at],
    ['Qualified', request.qualified_at],
    ['Onboarded', request.onboarded_at],
    ['Closed', request.closed_at],
  ];
  return stages
    .filter((stage): stage is [string, string] => stage[1] !== null)
    .map(([label, at]) => ({ label, at }));
}

/** A website URL safe to render as a link (http/https only), else null. */
export function safeExternalUrl(value: string | null): string | null {
  if (!value) return null;
  const candidate = /^[a-z][a-z0-9+.-]*:/i.test(value) ? value : `https://${value}`;
  try {
    const url = new URL(candidate);
    return url.protocol === 'http:' || url.protocol === 'https:' ? url.toString() : null;
  } catch {
    return null;
  }
}

export * from './websiteLeadsOutreach.logic';
